import icons from 'url:../../img/icons.svg';
// the parent view, and sub-views will inherit this!
export default class View {
  _data;

  // this receives data and will set this._data to the data it received.  We didn't put the const markup in render because the idea will be to use render on other views and classes.
  // this is part of the public API.
  // how to use jsdocs.  Goto jsdoc.app for more info
  // to write documentation, write a multi-line comment and then a second star.  "data" and "render" are our parameters
  /**
   * Render the received object to the DOM
   *
   * @param {Object | Object[]} data Data is the name of the parameter is this represents the data to be rendered (i.e. a recipe)
   * @param {boolean} [render=true] If this is false, then create markup string instead of rendering to the DOM.  What's the advantage?  If you work with someone else, then that programmer should understand what you did.
   * @returns {undefined | string} What does this render function return?  Either nothing or a string
   * @this {Object} Here, the "this" keyword points to the View object itself
   * @author Jonas Schmedtmann
   * @todo Finish implementation
   */
  render(data, render = true) {
    // there is a helper function on the Array constructor which checks if it is an array and the length is 0.  Exit the function right away and render the error
    if (!data || (Array.isArray(data) && data.length === 0))
      return this.renderError();
    this._data = data;
    // now, render the data by putting into the view.  Don't put it in render since render will be common to all of the classes.
    const markup = this._generateMarkup();

    // return the generated markup if false.  Why not call generateMarkup itself?  Because we need it to set the data property to the data we passed in.
    if (!render) return markup;
    this._clear();
    // render the HTML, and put it after the start.
    this._parentElement.insertAdjacentHTML('afterbegin', markup);
  }

  update(data) {
    this._data = data;
    const newMarkup = this._generateMarkup();

    const newDOM = document.createRange().createContextualFragment(newMarkup);
    const newElements = Array.from(newDOM.querySelectorAll('*'));

    const currentElements = Array.from(
      this._parentElement.querySelectorAll('*')
    );

    newElements.forEach((newEl, i) => {
      const currentElement = currentElements[i];

      if (
        !newEl.isEqualNode(currentElement) &&
        // ? is there in case firstChild element doesn't exist
        newEl.firstChild?.nodeValue.trim() !== ''
      ) {
        currentElement.textContent = newEl.textContent;
      }

      // update the changed attributes
      if (!newEl.isEqualNode(currentElement))
        Array.from(newEl.attributes).forEach(attribute =>
          currentElement.setAttribute(attribute.name, attribute.value)
        );
    });
  }

  // abstract _clear to make it a little cleaner.  More importantly, it becomes reuseable!
  _clear() {
    this._parentElement.innerHTML = '';
  }

  // not business logic, so it belongs here in the view.  This is a PUBLIC method.
  renderSpinner() {
    const markup = `
    <div class="spinner">
    <svg>
      <use href="${icons}#icon-loader"></use>
    </svg>
  </div>
    `;
    // this function returns an HTML string, and render is responsible for putting the HTML on the page.
    this._clear();
    this._parentElement.insertAdjacentHTML('afterbegin', markup);
  }

  renderError(message = this._errorMessage) {
    const markup = `
    <div class="error">
          <div>
            <svg>
              <use href="${icons}#icon-alert-triangle"></use>
            </svg>
          </div>
          <p>${message}</p>
        </div>
    `;
    this._clear();
    this._parentElement.insertAdjacentHTML('afterbegin', markup);
  }

  renderMessage(message = this._message) {
    const markup = `
    <div class="message">
          <div>
            <svg>
              <use href="${icons}#icon-smile"></use>
            </svg>
          </div>
          <p>${message}</p>
        </div>
    `;
    this._clear();
    this._parentElement.insertAdjacentHTML('afterbegin', markup);
  }
}

/*  
 // this is a private method.  Call it with const markup.  Where is recipe.data (like, image, title, cookingTime, etc)?  In this._data.  The recipe is loaded in controller.js and stored in the state object.  Then, take model.state.recipe and pass it into the render method.  Then, render takes the data and stores it in this._data.  Then, we can use it anywhere.
  _generateMarkup() {
    return `
    <figure class="recipe__fig">
    <img src="${this._data.image}" alt="${
      this._data.title
    }" class="recipe__img" />
    <h1 class="recipe__title">
      <span>${this._data.title}</span>
    </h1>
  </figure>

  <div class="recipe__details">
    <div class="recipe__info">
      <svg class="recipe__info-icon">
        <use href="${icons}#icon-clock"></use>
      </svg>
      <span class="recipe__info-data recipe__info-data--minutes">${
        this._data.cookingTime
      }</span>
      <span class="recipe__info-text">minutes</span>
    </div>
    <div class="recipe__info">
      <svg class="recipe__info-icon">
        <use href="${icons}#icon-users"></use>
      </svg>
      <span class="recipe__info-data recipe__info-data--people">${
        this._data.servings
      }</span>
      <span class="recipe__info-text">servings</span>

      <div class="recipe__info-buttons">
        <button class="btn--tiny btn--increase-servings">
          <svg>
            <use href="${icons}#icon-minus-circle"></use>
          </svg>
        </button>
        <button class="btn--tiny btn--increase-servings">
          <svg>
            <use href="${icons}#icon-plus-circle"></use>
          </svg>
        </button>
      </div>
    </div>

    <div class="recipe__user-generated">
      <svg>
        <use href="${icons}#icon-user"></use>
      </svg>
    </div>
    <button class="btn--round">
      <svg class="">
        <use href="${icons}#icon-bookmark-fill"></use>
      </svg>
    </button>
  </div>

  <div class="recipe__ingredients">
    <h2 class="heading--2">Recipe ingredients</h2>
    <ul class="recipe__ingredient-list">
    ${this._data.ingredients.map(this._generateMarkupIngredient).join('')}
      <li class="recipe__ingredient">
        <svg class="recipe__icon">
          <use href="${icons}#icon-check"></use>
        </svg>
        <div class="recipe__quantity">1000</div>
        <div class="recipe__description">
          <span class="recipe__unit">g</span>
          pasta
        </div>
      </li>
    </ul>
  </div>

  <div class="recipe__directions">
    <h2 class="heading--2">How to cook it</h2>
    <p class="recipe__directions-text">
      This recipe was carefully designed and tested by
      <span class="recipe__publisher">${
        this._data.publisher
      }</span>. Please check out
      directions at their website.
    </p>
    <a
      class="btn--small recipe__btn"
      href="${this._data.sourceUrl}"
      target="_blank"
    >
      <span>Directions</span>
      <svg class="search__icon">
        <use href="${icons}#icon-arrow-right"></use>
      </svg>
    </a>
  </div>
    `;
  }
*/
